import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Button, Badge, InputGroup, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { 
  FaBell, FaExclamationTriangle, FaHourglassHalf, FaCheckCircle, 
  FaShoppingCart, FaFilePrescription, FaCreditCard, FaTruck, 
  FaSearch, FaCheckDouble, FaTrashAlt, FaSyncAlt, FaExternalLinkAlt, 
  FaCapsules, FaClock, FaTimes, FaFilter, FaInfoCircle, FaShieldAlt
} from 'react-icons/fa';
import { get } from '../utils/api';
import { useToast } from './ToastContext';
import './NotificationManagement.css';
import ProgressDialog from './ProgressDialog';
import NoDataCard from './NoDataCard';

const Notifications = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState(() => {
    try {
      const saved = localStorage.getItem('read_notification_ids');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all' | 'inventory' | 'orders' | 'prescriptions' | 'payments' | 'delivery'
  const [priorityFilter, setPriorityFilter] = useState('all'); // 'all' | 'critical' | 'warning' | 'info'
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch live system alerts from APIs
  const fetchLiveAlerts = useCallback(async () => {
    setLoading(true);
    const alertList = [];

    try {
      // 1. Fetch dashboard widgets for low stock & expiring items
      const widgetsRes = await get('/reports/dashboard/widgets').catch(() => null);
      if (widgetsRes && widgetsRes.success && widgetsRes.data) {
        const { lowStock = [], expiring = [] } = widgetsRes.data;

        // Low stock alerts
        lowStock.forEach((item, i) => {
          alertList.push({
            id: `stock-${item.medicine_id || i}`,
            category: 'inventory',
            priority: item.stock <= 5 ? 'critical' : 'warning',
            title: `Low Stock Alert: ${item.name}`,
            message: `Only ${item.stock} unit(s) left in inventory. Reorder batch from supplier immediately.`,
            time: 'Live Alert',
            timestamp: new Date().toISOString(),
            link: '/batches',
            actionText: 'Manage Batches',
            icon: FaCapsules,
            badgeClass: 'stock-badge'
          });
        });

        // Expiring batch alerts
        expiring.forEach((item, i) => {
          const expDate = item.expiry_date ? new Date(item.expiry_date).toLocaleDateString('en-GB') : 'Soon';
          alertList.push({
            id: `exp-${item.batch_id || i}`,
            category: 'inventory',
            priority: 'warning',
            title: `Batch Expiry Warning: ${item.medicine_name || item.name}`,
            message: `Batch #${item.batch_number || item.batch_id} expires on ${expDate}. Review stock movement.`,
            time: 'Attention Needed',
            timestamp: new Date().toISOString(),
            link: '/batches',
            actionText: 'View Batch',
            icon: FaHourglassHalf,
            badgeClass: 'expiry-badge'
          });
        });
      }

      // 2. Fetch pending prescriptions
      const rxRes = await get('/prescriptions').catch(() => null);
      if (rxRes && rxRes.success && Array.isArray(rxRes.data)) {
        const pendingRx = rxRes.data.filter(p => (p.status || '').toLowerCase() === 'pending_verification' || (p.status || '').toLowerCase() === 'pending');
        if (pendingRx.length > 0) {
          alertList.push({
            id: `rx-pending-summary`,
            category: 'prescriptions',
            priority: 'critical',
            title: `${pendingRx.length} Prescription(s) Awaiting Pharmacist Review`,
            message: `Patients have uploaded prescription notes requiring validation before order dispatch.`,
            time: 'Pending Action',
            timestamp: new Date().toISOString(),
            link: '/prescriptions',
            actionText: 'Review Prescriptions',
            icon: FaFilePrescription,
            badgeClass: 'rx-badge'
          });
        }
      }

      // 3. Fetch orders needing review / pending dispatch
      const ordersRes = await get('/orders?limit=10').catch(() => null);
      if (ordersRes && ordersRes.success && Array.isArray(ordersRes.data)) {
        const pendingOrders = ordersRes.data.filter(o => (o.status || '').toLowerCase() === 'pending');
        if (pendingOrders.length > 0) {
          alertList.push({
            id: `orders-pending-summary`,
            category: 'orders',
            priority: 'info',
            title: `${pendingOrders.length} New Online Order(s) Received`,
            message: `Unconfirmed customer orders in queue ready for item packaging and dispatch.`,
            time: 'Recent',
            timestamp: new Date().toISOString(),
            link: '/orders',
            actionText: 'View Orders',
            icon: FaShoppingCart,
            badgeClass: 'order-badge'
          });
        }
      }

      // 4. Fetch recent payments
      const payRes = await get('/payments?limit=5').catch(() => null);
      if (payRes && payRes.success && Array.isArray(payRes.data)) {
        const failedPayments = payRes.data.filter(p => (p.payment_status || '').toLowerCase() === 'failed');
        if (failedPayments.length > 0) {
          alertList.push({
            id: `pay-failed-summary`,
            category: 'payments',
            priority: 'warning',
            title: `Payment Reconciliation Alert: ${failedPayments.length} Failed Payment(s)`,
            message: `One or more transactions failed online verification. Review payment ledger.`,
            time: 'Payment Alert',
            timestamp: new Date().toISOString(),
            link: '/payments',
            actionText: 'Audit Ledger',
            icon: FaCreditCard,
            badgeClass: 'pay-badge'
          });
        }
      }

      // 5. Default fallback alerts if all stores are pristine
      if (alertList.length === 0) {
        alertList.push(
          {
            id: 'sys-all-clear',
            category: 'orders',
            priority: 'success',
            title: 'System Operating Normally',
            message: 'All inventory levels are healthy, prescriptions are reviewed, and payments are balanced.',
            time: 'Just now',
            timestamp: new Date().toISOString(),
            link: '/dashboard',
            actionText: 'Go to Dashboard',
            icon: FaCheckCircle,
            badgeClass: 'success-badge'
          }
        );
      }

      setNotifications(alertList);
    } catch (err) {
      console.error('Failed to load notification alerts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLiveAlerts();
  }, [fetchLiveAlerts]);

  // Mark single as read
  const handleMarkAsRead = (id) => {
    const next = [...new Set([...readIds, id])];
    setReadIds(next);
    localStorage.setItem('read_notification_ids', JSON.stringify(next));
  };

  // Mark all as read
  const handleMarkAllRead = () => {
    const allIds = notifications.map(n => n.id);
    const next = [...new Set([...readIds, ...allIds])];
    setReadIds(next);
    localStorage.setItem('read_notification_ids', JSON.stringify(next));
    showToast('All notifications marked as read.', 'success');
  };

  // Clear read notifications
  const handleClearRead = () => {
    setNotifications(prev => prev.filter(n => !readIds.includes(n.id)));
    showToast('Read notifications cleared.', 'info');
  };

  // Filtered Notifications
  const filteredNotifications = useMemo(() => {
    return notifications.filter(n => {
      if (categoryFilter !== 'all' && n.category !== categoryFilter) return false;
      if (priorityFilter !== 'all' && n.priority !== priorityFilter) return false;
      if (searchTerm.trim() !== '') {
        const q = searchTerm.toLowerCase();
        const matchTitle = (n.title || '').toLowerCase().includes(q);
        const matchMsg = (n.message || '').toLowerCase().includes(q);
        if (!matchTitle && !matchMsg) return false;
      }
      return true;
    });
  }, [notifications, categoryFilter, priorityFilter, searchTerm]);

  // Metrics
  const metrics = useMemo(() => {
    const unreadCount = notifications.filter(n => !readIds.includes(n.id)).length;
    const criticalCount = notifications.filter(n => n.priority === 'critical').length;
    const inventoryCount = notifications.filter(n => n.category === 'inventory').length;
    const rxCount = notifications.filter(n => n.category === 'prescriptions').length;
    return { unreadCount, criticalCount, inventoryCount, rxCount };
  }, [notifications, readIds]);

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'critical':
        return <span className="ntf-priority-pill ntf-critical"><FaExclamationTriangle className="me-1" /> High Alert</span>;
      case 'warning':
        return <span className="ntf-priority-pill ntf-warning"><FaHourglassHalf className="me-1" /> Warning</span>;
      case 'success':
        return <span className="ntf-priority-pill ntf-success"><FaCheckCircle className="me-1" /> Healthy</span>;
      default:
        return <span className="ntf-priority-pill ntf-info"><FaInfoCircle className="me-1" /> Info</span>;
    }
  };

  return (
    <Container fluid className="ntf-mgmt-page px-3 px-lg-4 py-3">
      <ProgressDialog show={loading && notifications.length === 0} message="Loading alerts & notifications..." />

      {/* Header & KPI Summary */}
      <div className="ntf-header-panel mb-4 p-3 p-lg-4 rounded glassmorphic-panel">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-3">
            <div className="ntf-master-icon">
              <FaBell />
            </div>
            <div>
              <h3 className="ntf-title mb-0">System Alerts & Notifications</h3>
              <p className="ntf-subtitle mb-0 text-muted">Real-time pharmacy inventory thresholds, prescription tasks, order feeds & payment audits.</p>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Button 
              variant="outline-primary" 
              size="sm" 
              onClick={handleMarkAllRead} 
              disabled={metrics.unreadCount === 0}
              className="ntf-action-btn d-flex align-items-center gap-1"
            >
              <FaCheckDouble /> Mark All Read
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={handleClearRead} 
              className="ntf-action-btn d-flex align-items-center gap-1"
            >
              <FaTrashAlt /> Clear Read
            </Button>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              onClick={fetchLiveAlerts} 
              className="ntf-action-btn d-flex align-items-center gap-1"
            >
              <FaSyncAlt className={loading ? 'fa-spin' : ''} /> Refresh
            </Button>
          </div>
        </div>

        {/* KPI Metrics Row */}
        <Row className="g-3">
          <Col xs={6} md={3}>
            <div className="ntf-kpi-card">
              <div className="kpi-icon unread">
                <FaBell />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-primary">{metrics.unreadCount}</div>
                <div className="kpi-label">Unread Alerts</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="ntf-kpi-card">
              <div className="kpi-icon critical">
                <FaExclamationTriangle />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-danger">{metrics.criticalCount}</div>
                <div className="kpi-label">Critical Actions</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="ntf-kpi-card">
              <div className="kpi-icon inventory">
                <FaCapsules />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-warning">{metrics.inventoryCount}</div>
                <div className="kpi-label">Stock & Expiry</div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="ntf-kpi-card">
              <div className="kpi-icon rx">
                <FaFilePrescription />
              </div>
              <div className="kpi-content">
                <div className="kpi-value text-info">{metrics.rxCount}</div>
                <div className="kpi-label">Rx Reviews</div>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* Filter & Search Toolbar */}
      <div className="ntf-toolbar-panel mb-4 p-3 rounded glassmorphic-panel">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          {/* Search Box */}
          <div className="ntf-search-wrapper flex-grow-1">
            <InputGroup className="ntf-search-group">
              <InputGroup.Text className="bg-transparent border-0 pe-1 text-muted">
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search alerts, medicine name, order ref, keywords..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ntf-search-input border-0 ps-1"
              />
              {searchTerm && (
                <Button 
                  variant="link" 
                  onClick={() => setSearchTerm('')} 
                  className="ntf-clear-search text-muted border-0"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Category Tabs */}
          <div className="ntf-category-chips d-flex align-items-center gap-2 flex-wrap">
            <button 
              type="button" 
              className={`ntf-tab-chip ${categoryFilter === 'all' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('all')}
            >
              All Alerts ({notifications.length})
            </button>
            <button 
              type="button" 
              className={`ntf-tab-chip ${categoryFilter === 'inventory' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('inventory')}
            >
              💊 Stock & Expiry ({metrics.inventoryCount})
            </button>
            <button 
              type="button" 
              className={`ntf-tab-chip ${categoryFilter === 'prescriptions' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('prescriptions')}
            >
              🧾 Prescriptions ({metrics.rxCount})
            </button>
            <button 
              type="button" 
              className={`ntf-tab-chip ${categoryFilter === 'orders' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('orders')}
            >
              🛒 Orders
            </button>
            <button 
              type="button" 
              className={`ntf-tab-chip ${categoryFilter === 'payments' ? 'active' : ''}`}
              onClick={() => setCategoryFilter('payments')}
            >
              💳 Payments
            </button>
          </div>

          {/* Priority Dropdown */}
          <div className="d-flex align-items-center gap-2">
            <Form.Select 
              value={priorityFilter} 
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="ntf-select-pill"
              size="sm"
            >
              <option value="all">⚡ All Priorities</option>
              <option value="critical">🚨 Critical Only</option>
              <option value="warning">⚠️ Warnings</option>
              <option value="info">ℹ️ Information</option>
            </Form.Select>
          </div>
        </div>
      </div>

      {/* Main Notification Cards Feed */}
      {filteredNotifications.length === 0 && !loading ? (
        <div className="ntf-no-data-box py-5 text-center rounded glassmorphic-panel">
          <NoDataCard 
            title="No Alerts Found"
            message={searchTerm || categoryFilter !== 'all' ? "No notifications match your active search or filter criteria." : "All systems are operating normally. No new notifications."}
            icon="🔔"
            actionButton={
              searchTerm || categoryFilter !== 'all' ? (
                <Button variant="primary" size="sm" onClick={() => { setSearchTerm(''); setCategoryFilter('all'); setPriorityFilter('all'); }}>
                  Reset Filters
                </Button>
              ) : null
            }
          />
        </div>
      ) : (
        <div className="ntf-feed-list">
          {filteredNotifications.map((item) => {
            const isRead = readIds.includes(item.id);
            const Icon = item.icon || FaBell;

            return (
              <div 
                className={`ntf-card-item glassmorphic-panel ${isRead ? 'read' : 'unread'} ${item.priority}`}
                key={item.id}
                onClick={() => handleMarkAsRead(item.id)}
              >
                <div className="ntf-card-inner p-3 d-flex align-items-start gap-3">
                  {/* Category / Priority Icon */}
                  <div className={`ntf-icon-box ${item.priority}`}>
                    <Icon />
                  </div>

                  {/* Body Content */}
                  <div className="ntf-content-body flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-1">
                      <div className="d-flex align-items-center gap-2">
                        <h6 className="ntf-card-title mb-0">{item.title}</h6>
                        {!isRead && <span className="ntf-unread-dot" title="Unread alert"></span>}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {getPriorityBadge(item.priority)}
                        <span className="ntf-time-tag"><FaClock className="me-1 small" />{item.time}</span>
                      </div>
                    </div>

                    <p className="ntf-card-message mb-2">{item.message}</p>

                    {/* Action Bar */}
                    <div className="ntf-action-row d-flex align-items-center justify-content-between pt-2 border-top">
                      <div className="ntf-meta-info small text-muted">
                        Category: <strong className="text-capitalize ntf-category-text">{item.category}</strong>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        {!isRead && (
                          <button 
                            type="button" 
                            className="ntf-mark-read-btn"
                            onClick={(e) => { e.stopPropagation(); handleMarkAsRead(item.id); }}
                          >
                            Mark Read
                          </button>
                        )}
                        {item.link && (
                          <Button 
                            variant="primary" 
                            size="sm" 
                            className="ntf-jump-btn d-flex align-items-center gap-1"
                            onClick={(e) => { e.stopPropagation(); navigate(item.link); }}
                          >
                            {item.actionText || 'Take Action'} <FaExternalLinkAlt className="small" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Container>
  );
};

export default Notifications;
